import { useFxWorkspaceContext } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import {
  buildChildrenIndex,
  flattenVisibleRows,
  toggleCascadingTreeSelection,
} from '@trimble/ui-components/solidjs';
import { Component, createMemo, createSignal, For, Show } from 'solid-js';

import { useActiveFxWorkspaceTree } from '../../selections/tree/useActiveFxWorkspaceTree';
import type { ExtractionSourceListProps } from './ExtractionSourceList.types';

export const ExtractionSourceList: Component<ExtractionSourceListProps> = (
  props
) => {
  const fxWorkspace = useFxWorkspaceContext();
  const { nodes } = useActiveFxWorkspaceTree();
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<string>>(
    new Set()
  );

  const referenceCount = createMemo(() =>
    fxWorkspace.workspaceReferenceCount(props.activeWorkspaceId)
  );

  const rows = createMemo(() => {
    const childrenIndex = buildChildrenIndex(nodes());
    return flattenVisibleRows(
      (parentId) => childrenIndex.get(parentId) ?? [],
      () => true
    ).filter((row) => row.depth > 0);
  });

  const toggle = (id: string): void => {
    setSelectedIds((previous) =>
      toggleCascadingTreeSelection(nodes(), previous, id)
    );
  };

  return (
    <div class="flex flex-col gap-2" data-testid="extraction-source-section">
      <div class="flex flex-col gap-1">
        <p
          class="m-0 text-sm font-semibold"
          data-testid="extraction-source-title"
          textContent={t(
            'mobileMapping.fxWorkspace.extraction.targetDataTitle'
          )}
        />
        <p
          class="m-0 text-sm text-muted"
          data-testid="extraction-source-subtitle"
          textContent={t(
            'mobileMapping.fxWorkspace.extraction.targetDataSubtitle'
          )}
        />
      </div>
      <Show
        when={referenceCount() > 0}
        fallback={
          <p
            class="m-0 text-sm text-muted"
            data-testid="extraction-source-list-empty"
            textContent={t('mobileMapping.fxWorkspace.emptyWorkspace.title')}
          />
        }
      >
        <div
          class="flex max-h-[30vh] flex-col gap-2 overflow-y-auto overflow-x-hidden px-1"
          data-testid="extraction-source-list"
        >
          <For each={rows()}>
            {(row) => (
              <div
                class="flex min-w-0 items-center gap-2"
                data-testid={`extraction-source-row-${row.id}`}
                style={{ 'padding-left': `${row.depth - 1}rem` }}
              >
                <modus-wc-checkbox
                  size="md"
                  class="shrink-0"
                  attr:data-testid={`extraction-source-${row.id}`}
                  attr:aria-label={row.node.label}
                  value={selectedIds().has(row.id)}
                  on:inputChange={() => toggle(row.id)}
                />
                <span
                  class="cursor-pointer truncate text-sm"
                  data-testid={`extraction-source-label-${row.id}`}
                  title={row.node.label}
                  textContent={row.node.label}
                  onClick={() => toggle(row.id)}
                />
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};
